import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  listAdminUsers,
  createAdminUser,
  updateAdminUserRole,
  deleteAdminUser,
  AdminUserRow,
  AdminRole,
} from '@/lib/api';

const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  editor: 'Editor',
};

const ROLE_OPTIONS: AdminRole[] = ['super_admin', 'admin', 'editor'];

export default function UsersPage() {
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', fullName: '', role: 'editor' as AdminRole });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listAdminUsers());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (form.password.length < 8) {
      setFormError('Password must be at least 8 characters long.');
      return;
    }

    setSaving(true);
    try {
      await createAdminUser({
        email: form.email,
        password: form.password,
        fullName: form.fullName,
        role: form.role,
      });
      setForm({ email: '', password: '', fullName: '', role: 'editor' });
      setShowAdd(false);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (row: AdminUserRow, role: AdminRole) => {
    setBusyId(row.id);
    try {
      await updateAdminUserRole(row.id, role, row.full_name);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row: AdminUserRow) => {
    if (!window.confirm(`Delete ${row.email}? This action cannot be undone.`)) return;
    setBusyId(row.id);
    try {
      await deleteAdminUser(row.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading font-semibold text-xl text-foreground-900">Team &amp; Admins</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Add, remove, and manage the roles of the people who can access this dashboard.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setFormError(null);
            setShowAdd((v) => !v);
          }}
          className="btn-primary whitespace-nowrap"
        >
          <i className="ri-user-add-line" aria-hidden="true" />
          Add User
        </button>
      </div>

      {!isSuperAdmin && (
        <div className="mb-4 px-4 py-3 rounded-md bg-accent-100 text-accent-900 text-sm border border-accent-300">
          You are viewing users in read-only mode. Only a super admin can add, remove, or change roles.
        </div>
      )}

      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
          {error}
          <button type="button" onClick={load} className="ml-3 underline">
            Retry
          </button>
        </div>
      )}

      {showAdd && isSuperAdmin && (
        <form onSubmit={handleAdd} className="mb-6 bg-white rounded-lg border border-background-200 p-6">
          <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Add New User</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Full Name</label>
              <input
                type="text"
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                required
                className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300"
                placeholder="name@tgmci.org"
              />
            </div>
            <div>
              <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                autoComplete="new-password"
                className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300"
                placeholder="At least 8 characters"
              />
            </div>
            <div>
              <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as AdminRole })}
                className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 outline-none focus:ring-2 focus:ring-primary-300 bg-white"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {formError && (
            <div className="mt-4 px-3 py-2.5 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
              {formError}
            </div>
          )}

          <div className="mt-4 flex gap-3">
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Creating...' : 'Create User'}
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="btn-outline"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-16 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl inline-block" aria-hidden="true" />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center bg-white rounded-lg border border-background-200">
          <i className="ri-team-line text-4xl text-foreground-300" aria-hidden="true" />
          <p className="mt-3 text-sm text-foreground-500">No users yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100 text-left text-xs uppercase tracking-wider text-foreground-500">
                  <th className="px-4 py-3 font-label font-semibold">Name</th>
                  <th className="px-4 py-3 font-label font-semibold">Email</th>
                  <th className="px-4 py-3 font-label font-semibold">Role</th>
                  <th className="px-4 py-3 font-label font-semibold">Added</th>
                  <th className="px-4 py-3 font-label font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isSelf = row.id === currentUser?.id;
                  return (
                    <tr key={row.id} className="border-t border-background-100">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground-800">
                          {row.full_name || '—'}
                          {isSelf && <span className="ml-2 text-xs text-primary-600">(you)</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-foreground-600">{row.email}</td>
                      <td className="px-4 py-3">
                        {isSuperAdmin && !isSelf ? (
                          <select
                            value={row.role}
                            disabled={busyId === row.id}
                            onChange={(e) => handleRoleChange(row, e.target.value as AdminRole)}
                            className="px-2 py-1 rounded-md text-xs border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                          >
                            {ROLE_OPTIONS.map((r) => (
                              <option key={r} value={r}>
                                {ROLE_LABELS[r]}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-900">
                            {ROLE_LABELS[row.role] ?? row.role}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-foreground-500">
                        {new Date(row.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isSuperAdmin && !isSelf && (
                          <button
                            type="button"
                            disabled={busyId === row.id}
                            onClick={() => handleDelete(row)}
                            className="px-3 py-1.5 rounded-md bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 cursor-pointer disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}